<template>
  <div class="self-order-qr-code">
    <!-- セルフオーダー状態表示・操作ボタン -->
    <v-card
      outlined
      class="mb-2"
    >
      <v-card-text class="d-flex align-center justify-space-between py-1 px-3">
        <div class="d-flex align-center">
          <v-icon
            :color="isSelfOrderOpen ? 'success' : 'grey'"
            small
          >
            {{ icons.mdiQrcode }}
          </v-icon>
          <v-switch
            :input-value="isSelfOrderOpen"
            :loading="isToggling"
            :disabled="isToggling"
            hide-details
            dense
            class="mt-0 pt-0 ml-1"
            @change="toggleSelfOrder"
          />
        </div>

        <div
          v-if="isSelfOrderOpen"
          class="d-flex align-center"
        >
          <v-btn
            x-small
            color="primary"
            class="mr-1"
            :loading="isLoadingQr"
            @click="showQrCodeDialog"
          >
            <v-icon
              left
              x-small
            >
              {{ icons.mdiQrcode }}
            </v-icon>
            QR
          </v-btn>

          <v-btn
            x-small
            outlined
            color="primary"
            :loading="isCopyingUrl"
            @click="copyUrl"
          >
            <v-icon
              left
              x-small
            >
              {{ icons.mdiContentCopy }}
            </v-icon>
            URL
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- QRコード表示ダイアログ -->
    <v-dialog
      v-model="isQrDialogOpen"
      max-width="400"
      persistent
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>セルフオーダー QRコード</span>
          <v-btn
            icon
            @click="closeQrDialog"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="text-center">
          <!-- テーブル情報 -->
          <div class="mb-4">
            <div class="text-h6">
              {{ clubName }}
            </div>
            <div class="text-subtitle-1">
              {{ hallNameDisplay }} - {{ tableDisplayName }}
            </div>
          </div>

          <!-- QRコード -->
          <div
            ref="qrContainer"
            class="qr-code-container"
          >
            <canvas
              v-if="qrCodeUrl"
              id="self-order-qrcode"
              class="qr-canvas"
            />
            <v-progress-circular
              v-else
              indeterminate
              color="primary"
            />
          </div>

          <!-- セキュリティレベル表示 -->
          <v-chip
            v-if="securityLevel"
            small
            :color="securityLevelColor"
            class="mt-3"
          >
            {{ securityLevelLabel }}
          </v-chip>
        </v-card-text>

        <v-card-actions class="justify-center pb-4">
          <v-btn
            color="primary"
            outlined
            @click="printQrCode"
          >
            <v-icon left>
              {{ icons.mdiPrinter }}
            </v-icon>
            印刷
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 印刷用の非表示エリア -->
    <div
      v-show="false"
      ref="printArea"
      class="print-area"
    >
      <div class="print-content">
        <h2>{{ clubName }}</h2>
        <h3>{{ hallName }} - {{ tableDisplayName }}</h3>
        <canvas id="self-order-qrcode-print" />
        <p class="print-instruction">
          QRコードを読み取って注文できます
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref, computed, nextTick, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiQrcode,
  mdiClose,
  mdiPrinter,
  mdiContentCopy,
} from '@mdi/js'
import QrCode from 'qrcode'
import TableApi from '@/api/waiter/Table'
import useCurrentData from '@/views/composable/useCurrentData'

export default {
  name: 'SelfOrderQrCode',
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => value === null || !Number.isNaN(Number(value)),
      default: null,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '',
    },
    selfOrderStatus: {
      type: String,
      default: null,
    },
    hallName: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const { currentClub } = useCurrentData()

    // State
    const isQrDialogOpen = ref(false)
    const isLoadingQr = ref(false)
    const isCopyingUrl = ref(false)
    const isToggling = ref(false)
    const qrCodeUrl = ref(null)
    const securityLevel = ref(null)
    const localSelfOrderStatus = ref(props.selfOrderStatus)

    // Computed
    const isSelfOrderOpen = computed(() => localSelfOrderStatus.value === 'open')

    const clubName = computed(() => currentClub.value?.name || '')
    const hallNameDisplay = computed(() => props.hallName || '')

    const tableDisplayName = computed(() => {
      const prefix = props.tablePrefix || ''
      const suffix = props.tableSuffix || ''

      return `${prefix}${props.tableNumber}${suffix}`
    })

    const securityLevelLabel = computed(() => {
      const labels = {
        staff_only: 'スタッフ専用',
        standard: '標準',
        secure: 'セキュア（動的QR）',
      }

      return labels[securityLevel.value] || ''
    })

    const securityLevelColor = computed(() => {
      const colors = {
        staff_only: 'info',
        standard: 'warning',
        secure: 'success',
      }

      return colors[securityLevel.value] || 'grey'
    })

    // Methods
    const toggleSelfOrder = async value => {
      isToggling.value = true
      try {
        if (value) {
          await TableApi.selfOrderOpen(props.tableId)
          localSelfOrderStatus.value = 'open'
        } else {
          await TableApi.selfOrderClose(props.tableId)
          localSelfOrderStatus.value = 'closed'
        }
        emit('updated:self-order-status', localSelfOrderStatus.value)
      } catch (error) {
        console.error('セルフオーダー切り替えエラー:', error)
      } finally {
        isToggling.value = false
      }
    }

    const generateQrCode = canvasId => {
      const canvas = document.getElementById(canvasId)
      if (!canvas || !qrCodeUrl.value) return

      QrCode.toCanvas(canvas, qrCodeUrl.value, {
        width: 200,
        margin: 2,
      }, error => {
        if (error) console.error('QRコード生成エラー:', error)
      })
    }

    const showQrCodeDialog = async () => {
      isLoadingQr.value = true
      isQrDialogOpen.value = true

      try {
        const response = await TableApi.getSelfOrderQr(props.tableId)
        const data = response.data.data || response.data

        qrCodeUrl.value = data.url
        securityLevel.value = data.security_level

        await nextTick()
        generateQrCode('self-order-qrcode')
      } catch (error) {
        console.error('QRコード取得エラー:', error)
        isQrDialogOpen.value = false
      } finally {
        isLoadingQr.value = false
      }
    }

    const closeQrDialog = () => {
      isQrDialogOpen.value = false
      qrCodeUrl.value = null
    }

    const printQrCode = async () => {
      // 印刷用のQRコードを生成
      await nextTick()
      const printCanvas = document.getElementById('self-order-qrcode-print')
      if (printCanvas && qrCodeUrl.value) {
        QrCode.toCanvas(printCanvas, qrCodeUrl.value, {
          width: 300,
          margin: 2,
        }, error => {
          if (error) {
            console.error('印刷用QRコード生成エラー:', error)

            return
          }

          // 印刷ダイアログを開く
          const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <title>セルフオーダー QRコード</title>
              <style>
                body {
                  font-family: sans-serif;
                  text-align: center;
                  padding: 40px;
                }
                h2 { margin-bottom: 8px; }
                h3 { margin-bottom: 24px; color: #666; }
                .qr-code { margin: 24px auto; }
                .instruction {
                  margin-top: 24px;
                  color: #666;
                  font-size: 14px;
                }
              </style>
            </head>
            <body>
              <h2>${clubName.value}</h2>
              <h3>${hallNameDisplay.value} - ${tableDisplayName.value}</h3>
              <div class="qr-code">
                <img src="${printCanvas.toDataURL()}" />
              </div>
              <p class="instruction">QRコードを読み取って注文できます</p>
            </body>
            </html>
          `

          const printWindow = window.open('', '_blank')
          printWindow.document.write(printContent)
          printWindow.document.close()
          printWindow.focus()
          printWindow.print()
          printWindow.close()
        })
      }
    }

    const copyUrl = async () => {
      isCopyingUrl.value = true
      try {
        const response = await TableApi.getSelfOrderQr(props.tableId)
        const data = response.data.data || response.data
        const { url } = data

        await navigator.clipboard.writeText(url)
        vm.$toast.success('URLをコピーしました')
      } catch (error) {
        console.error('URLコピーエラー:', error)
        vm.$toast.error('URLのコピーに失敗しました')
      } finally {
        isCopyingUrl.value = false
      }
    }

    return {
      // State
      isQrDialogOpen,
      isLoadingQr,
      isCopyingUrl,
      isToggling,
      qrCodeUrl,
      securityLevel,

      // Computed
      isSelfOrderOpen,
      clubName,
      hallNameDisplay,
      tableDisplayName,
      securityLevelLabel,
      securityLevelColor,

      // Methods
      toggleSelfOrder,
      showQrCodeDialog,
      closeQrDialog,
      printQrCode,
      copyUrl,

      // Icons
      icons: {
        mdiQrcode,
        mdiClose,
        mdiPrinter,
        mdiContentCopy,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.self-order-qr-code {
  .qr-code-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }

  .qr-canvas {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.print-area {
  position: absolute;
  left: -9999px;
}
</style>
